import { Site } from '@envato/foundation-types'
import * as Cookies from 'js-cookie'
import random from 'lodash-es/random'
import { match } from 'react-router-dom'
import { Dispatch } from 'redux'
import { ThunkAction } from 'redux-thunk'

import { ICommonLocation } from '../utils/location/types'

import fetchSearchResults from '../../common/actions/searchPage'
import experimentsIds from '../../common/constants/experiments'
import { IRootState } from '../../common/reducers'
import { hasFeature } from '../../common/utils/features'
import { reportExperimentEnrolment } from '../analytics/experimentsAnalytics'

export enum TypeKeys {
  EXPERIMENT_DATA_RECEIVED = 'experimentDataReceived',
  EXPERIMENTS_READY = 'experimentsReady'
}

export enum VariantToSortMapping {
  VARIANT_0 = 'date',
  VARIANT_1 = 'date',
  VARIANT_2 = 'sales',
  VARIANT_3 = 'sales',
  VARIANT_4 = 'rating',
  VARIANT_5 = 'rating',
  VARIANT_6 = 'trending',
  VARIANT_7 = 'trending'
}

interface IExperimentData {
  running: string
  experiment_id: string
  variant: string
}

interface IExperimentDataReceivedAction {
  type: TypeKeys.EXPERIMENT_DATA_RECEIVED
  payload: IExperimentData
}

interface IExperimentsReadyAction {
  type: TypeKeys.EXPERIMENTS_READY
  payload: boolean
}

export const experimentsReady = (): IExperimentsReadyAction => ({
  type: TypeKeys.EXPERIMENTS_READY,
  payload: true
})

export const reloadResultsWithExperimentDefaultSortOnFirstPageLoad = (
  location: ICommonLocation,
  routeMatch: match<any>
) => (dispatch: Dispatch<IRootState>, getState: () => IRootState) => {
  const nextLocation = locationWithExperimentSortOrder(location, routeMatch)
  // In some case we already have the right sort order.
  // But, in order to make sure we record the right impressions, we need to reload here anyway
  return dispatch(fetchSearchResults(nextLocation, '', routeMatch))
}

export const locationWithExperimentSortOrder = (
  location: ICommonLocation,
  routeMatch: match<any>
): ICommonLocation => {
  const searchPageRegex = /\/(search)\/*/g
  const searchTermProvided =
    (location.query && location.query.term) ||
    routeMatch.params.term ||
    searchPageRegex.test(location.pathname)
  const sortOrderProvided = location.query && location.query.sort

  if (searchTermProvided || sortOrderProvided) {
    return location
  } else {
    const newSort = defaultSortOrderForSortExperiment()
    if (newSort) {
      return {
        ...location,
        query: {
          ...location.query,
          sort: newSort as string
        }
      }
    } else {
      return location
    }
  }
}

// will return undefined if server render
export const defaultSortOrderForSortExperiment = (): string | undefined => {
  const variant: number | undefined = readVariantFromCookie(
    experimentsIds.defaultSort
  )
  const variantString: any = variant ? `VARIANT_${variant}` : ''
  return VariantToSortMapping[variantString]
}

const readVariantFromCookie = (experimentId: string): number | undefined => {
  const experimentCookieKey = `experiment_${experimentId}`
  const experimentCookieValue = Cookies.get(experimentCookieKey)

  if (experimentCookieValue) {
    return parseInt(experimentCookieValue, 10)
  } else {
    return undefined
  }
}

export const readOrSelectExperimentVariantInCookie = (
  experimentId: string,
  variantCount: number = 3
): string => {
  const experimentCookieKey = `experiment_${experimentId}`
  const experimentCookieValue = Cookies.get(experimentCookieKey)

  // if the experiment cookie exist, simply fetch it from cookie
  if (experimentCookieValue) {
    return experimentCookieValue

    // otherwise generate a random variant and save it to cookie
  } else {
    // pick a random number between (0, N) 0,1 are variant A. 2,3 are variant B etc
    const selectedVariant = random(variantCount).toString()
    Cookies.set(experimentCookieKey, selectedVariant, {
      expires: 90, // 90 days
      path: '/'
    })

    return selectedVariant
  }
}

export const getExperimentVariant = (
  experimentId: string,
  variantCount: number = 3
): IExperimentDataReceivedAction => {
  const experimentData: IExperimentData = {
    running: 'true',
    experiment_id: experimentId,
    variant: readOrSelectExperimentVariantInCookie(experimentId, variantCount)
  }

  try {
    reportExperimentEnrolment({
      experimentId,
      variant: experimentData.variant
    })
  } finally {
    /* tslint:disable:no-unsafe-finally */
    return {
      type: TypeKeys.EXPERIMENT_DATA_RECEIVED,
      payload: experimentData
    }
    /* tslint:enable:no-unsafe-finally */
  }
}

export const setExperiments = (): ThunkAction<
  IExperimentDataReceivedAction | IExperimentsReadyAction | null,
  IRootState,
  void
> => {
  return (dispatch, getState) => {
    const { currentSite } = getState().common
    const { location } = getState().router
    const { experiments } = getState()

    const defaultSortExperimentEnabled = hasFeature(
      'defaultSort:enrolment',
      currentSite,
      location,
      experiments
    )

    if (defaultSortExperimentEnabled) {
      dispatch(getExperimentVariant(experimentsIds.defaultSort, 7))
    }

    return dispatch(experimentsReady())
  }
}
